/**
 * FikoSPHMotor - Main SPH Simulation Orchestrator
 *
 * This is the main entry point that coordinates all subsystems:
 * - WebGPU compute pipeline
 * - Particle rendering
 * - Element management (inlets, obstacles, sensors)
 * - Timeline and scenario control
 * - Analysis and reporting
 *
 * Architecture:
 * - Zero-allocation main loop
 * - Async sensor readback at 10Hz
 * - Event-driven state management
 * - Hot-swappable solver
 */

import { SPHSimulasyonConfig, SolverTipi, KaliteSeviyesi, varsayilanSPHConfig } from './FikoSPHParametreler';
import { MalzemeKutuphanesi } from './FikoSPHMalzemeKutuphanesi';
import { ElemanYonetici, ElemanTipi } from './elemanlar/ISPHEleman';
import { FikoDokumGirisi } from './elemanlar/FikoDokumGirisi';
import { FikoHavaDeligi } from './elemanlar/FikoHavaDeligi';
import { FikoEngel } from './elemanlar/FikoEngel';
import { FikoFiltre } from './elemanlar/FikoFiltre';
import { FikoBesleyici } from './elemanlar/FikoBesleyici';
import { FikoCikis } from './elemanlar/FikoCikis';
import { FikoSanalSensor } from './elemanlar/FikoSanalSensor';
import { FikoTermalEleman } from './elemanlar/FikoTermalEleman';
import { FikoZamanKontrol } from './elemanlar/FikoZamanKontrol';
import { FikoSPHGPUHesap } from './gpu/FikoSPHGPUHesap';
import { SolverFabrika } from './gpu/ISPHSolver';
import { FikoSPHRender, RenderModu, KameraData } from './render/FikoSPHRender';
import { FikoSPHAnaliz } from './analiz/FikoSPHAnaliz';
import { FikoSPHSensorGrafik } from './analiz/FikoSPHSensorGrafik';
import { FikoSPHKapYonetici } from './gpu/FikoSPHKapYonetici';

/**
 * Simulation state machine
 */
export enum SimulasyonDurumu {
  BOSTA = 'bosta',
  HAZIRLANIYOR = 'hazirlaniyor',
  CALISIYOR = 'calisiyor',
  DURAKLATILDI = 'duraklatildi',
  TAMAMLANDI = 'tamamlandi',
  HATA = 'hata'
}

/**
 * Event callbacks for simulation lifecycle
 */
export interface MotorOlaylari {
  onDurumDegisti?: (durum: SimulasyonDurumu) => void;
  onFrameTamamlandi?: (istatistik: any) => void;
  onHata?: (hata: Error) => void;
  onSolverDegisti?: (yeniSolver: string) => void;
  onElemanEklendi?: (elemanId: string, tip: ElemanTipi) => void;
  onElemanSilindi?: (elemanId: string) => void;
  onDolumTamamlandi?: () => void;
}

/**
 * Main SPH Simulation Motor
 *
 * Usage:
 * ```typescript
 * const motor = new FikoSPHMotor({ maxParcaciklar: 100000 });
 * await motor.basla(canvas);
 * motor.dokumGirisiEkle({ pozisyon: [0, 1, 0], debi: 0.5 });
 * motor.baslat();
 * ```
 */
export class FikoSPHMotor {
  // ============ STATE ============
  private durum: SimulasyonDurumu = SimulasyonDurumu.BOSTA;
  private config: SPHSimulasyonConfig;
  private olaylar: MotorOlaylari = {};

  // ============ SUBSYSTEMS ============
  private gpuHesap: FikoSPHGPUHesap | null = null;
  private render: FikoSPHRender | null = null;
  private analiz: FikoSPHAnaliz | null = null;
  private sensorGrafik: FikoSPHSensorGrafik | null = null;
  private elemanYonetici: ElemanYonetici;
  private zamanKontrol: FikoZamanKontrol;
  private kapYonetici: FikoSPHKapYonetici;
  private malzemeKutuphanesi: MalzemeKutuphanesi;

  // ============ WEBGPU ============
  private device: GPUDevice | null = null;
  private context: GPUCanvasContext | null = null;
  private adapter: GPUAdapter | null = null;

  // ============ ANIMATION ============
  private animasyonId: number = 0;
  private aktifParcacikSayisi: number = 0;
  private frameCounter: number = 0;
  private sonFrameZamani: number = 0;
  private _elemanBufferDirty: boolean = false;

  // ============ DOĞRUDAN SDF (voxel kafesten) ============
  private dogrudanSdfBuffer: GPUBuffer | null = null;
  private dogrudanSdfInfoBuffer: GPUBuffer | null = null;

  // ============ SENSOR READBACK ============
  private sensorReadbackPending: boolean = false;
  private sonSensorOkumaZamani: number = 0;
  private readonly SENSOR_READBACK_INTERVAL_MS = 100; // 10Hz

  // ============ CAMERA ============
  private kamera: KameraData = {
    view: new Float32Array(16),
    proj: new Float32Array(16),
    invProj: new Float32Array(16),
    pos: [0, 0, 5]
  };

  // ============ ERROR HANDLING ============
  private hataSayaci: number = 0;
  private readonly MAX_HATA_SAYISI = 10;

  /**
   * Constructor
   */
  constructor(config?: Partial<SPHSimulasyonConfig>) {
    this.config = { ...varsayilanSPHConfig(), ...config };

    // Initialize managers (these don't need GPU)
    this.elemanYonetici = new ElemanYonetici();
    this.zamanKontrol = new FikoZamanKontrol();
    this.kapYonetici = new FikoSPHKapYonetici();
    this.malzemeKutuphanesi = new MalzemeKutuphanesi();

    console.log('[FikoSPHMotor] Oluşturuldu', {
      maxParticle: this.config.global.maxParticle,
      solverTipi: this.config.global.solverTipi
    });
  }

  // ============================================================================
  // LIFECYCLE
  // ============================================================================

  /**
   * Initialize WebGPU and all subsystems
   */
  async basla(canvas: HTMLCanvasElement): Promise<boolean> {
    try {
      this.durumDegistir(SimulasyonDurumu.HAZIRLANIYOR);

      console.log('[FikoSPHMotor] WebGPU başlatılıyor...');

      // 1. Check WebGPU support
      if (!navigator.gpu) {
        throw new Error('WebGPU desteklenmiyor! Chrome Canary veya Edge Canary kullanın.');
      }

      // 2. Request adapter
      this.adapter = await navigator.gpu.requestAdapter({
        powerPreference: 'high-performance'
      });

      if (!this.adapter) {
        throw new Error('WebGPU adapter alınamadı!');
      }

      console.log('[FikoSPHMotor] Adapter bulundu:', this.adapter);

      // 3. Request device
      this.device = await this.adapter.requestDevice({
        requiredLimits: {
          maxStorageBufferBindingSize: this.adapter.limits.maxStorageBufferBindingSize,
          maxBufferSize: this.adapter.limits.maxStorageBufferBindingSize,
          maxComputeWorkgroupsPerDimension: this.adapter.limits.maxComputeWorkgroupsPerDimension,
          maxStorageBuffersPerShaderStage: Math.min(this.adapter.limits.maxStorageBuffersPerShaderStage, 16)
        }
      });

      this.device.lost.then((info) => {
        console.error('[FikoSPHMotor] Device kayboldu:', info);
        this.hataIsle(new Error(`WebGPU device kayboldu: ${info.message}`));
      }).catch((err) => {
        console.error('[FikoSPHMotor] device.lost promise rejected:', err);
        this.hataIsle(new Error(`WebGPU device.lost hatası: ${err?.message || err}`));
      });

      console.log('[FikoSPHMotor] Device oluşturuldu:', this.device.limits);

      // 4. Configure canvas context
      this.context = canvas.getContext('webgpu') as GPUCanvasContext;
      if (!this.context) {
        throw new Error('WebGPU canvas context alınamadı!');
      }

      const canvasFormat = navigator.gpu.getPreferredCanvasFormat();
      this.context.configure({
        device: this.device,
        format: canvasFormat,
        alphaMode: 'premultiplied',
        usage: GPUTextureUsage.RENDER_ATTACHMENT
      });

      console.log('[FikoSPHMotor] Canvas yapılandırıldı:', canvasFormat);

      // 5. Initialize GPU compute subsystem
      this.gpuHesap = new FikoSPHGPUHesap();
      await this.gpuHesap.basla({
        device: this.device,
        maxParticle: this.config.global.maxParticle,
        simBounds: this.config.global.simBounds,
        smoothingRadius: this.config.global.smoothingRadius
      });

      console.log('[FikoSPHMotor] GPU hesap sistemi hazır');

      // 5b. Initialize container manager with device
      this.kapYonetici.basla(this.device);

      // 6. Initialize renderer
      this.render = new FikoSPHRender();
      await this.render.basla(this.device, this.context, canvasFormat);

      console.log('[FikoSPHMotor] Render sistemi hazır');

      // 7. Initialize analysis
      this.analiz = new FikoSPHAnaliz();
      this.sensorGrafik = new FikoSPHSensorGrafik();

      console.log('[FikoSPHMotor] Analiz sistemi hazır');

      // 7b. Setup default camera (looking at origin from Z=3)
      this.setupVarsayilanKamera(canvas.width, canvas.height);

      // 8. Create default solver
      await this.solverDegistir(this.config.global.solverTipi);

      // 9. Setup timeline callbacks
      this.zamanKontrol.onOlay = (olay) => {
        if (olay.eylem === 'ac') {
          const e = this.elemanYonetici.al(olay.elemanId);
          if (e) {
            e.aktif = true;
            this._elemanBufferDirty = true;
          }
        } else if (olay.eylem === 'kapa') {
          const e = this.elemanYonetici.al(olay.elemanId);
          if (e) {
            e.aktif = false;
            this._elemanBufferDirty = true;
          }
        }
      };

      console.log('[FikoSPHMotor] Başlatma tamamlandı');
      this.durumDegistir(SimulasyonDurumu.BOSTA);

      return true;

    } catch (hata) {
      console.error('[FikoSPHMotor] Başlatma hatası:', hata);
      this.hataIsle(hata as Error);
      return false;
    }
  }

  /**
   * Start simulation loop
   */
  baslat(): void {
    if (this.durum === SimulasyonDurumu.CALISIYOR) {
      console.warn('[FikoSPHMotor] Simülasyon zaten çalışıyor');
      return;
    }

    if (!this.device || !this.gpuHesap || !this.render) {
      console.error('[FikoSPHMotor] Başlatma yapılmamış! Önce basla() çağırın.');
      return;
    }

    console.log('[FikoSPHMotor] Simülasyon başlatılıyor...');

    this.durumDegistir(SimulasyonDurumu.CALISIYOR);
    this.sonFrameZamani = performance.now();
    this.frameCounter = 0;
    this.zamanKontrol.baslat();

    this.animasyonDongusu();
  }

  /**
   * Pause simulation
   */
  duraklat(): void {
    if (this.durum !== SimulasyonDurumu.CALISIYOR) {
      return;
    }

    console.log('[FikoSPHMotor] Simülasyon duraklatıldı');
    this.durumDegistir(SimulasyonDurumu.DURAKLATILDI);
    this.zamanKontrol.duraklat();
  }

  /**
   * Resume from pause
   */
  devamEt(): void {
    if (this.durum !== SimulasyonDurumu.DURAKLATILDI) {
      return;
    }

    console.log('[FikoSPHMotor] Simülasyon devam ediyor');
    this.durumDegistir(SimulasyonDurumu.CALISIYOR);
    this.zamanKontrol.baslat();
    this.sonFrameZamani = performance.now();
  }

  /**
   * Stop simulation and cancel animation loop
   */
  durdur(): void {
    if (this.animasyonId !== 0) {
      cancelAnimationFrame(this.animasyonId);
      this.animasyonId = 0;
    }

    console.log('[FikoSPHMotor] Simülasyon durduruldu');
    this.durumDegistir(SimulasyonDurumu.BOSTA);
    this.zamanKontrol.duraklat();
  }

  /**
   * Full cleanup - destroy all GPU resources
   */
  temizle(): void {
    console.log('[FikoSPHMotor] Temizleme başlatılıyor...');

    this.durdur();

    // Cleanup subsystems
    this.gpuHesap?.temizle();
    this.render?.temizle();
    this.dogrudanSdfBuffer?.destroy();
    this.dogrudanSdfInfoBuffer?.destroy();
    this.dogrudanSdfBuffer = null;
    this.dogrudanSdfInfoBuffer = null;
    this.analiz?.sifirla();
    this.sensorGrafik = null;

    // Clear references
    this.gpuHesap = null;
    this.render = null;
    this.analiz = null;
    this.sensorGrafik = null;
    this.device = null;
    this.context = null;
    this.adapter = null;

    // Reset state
    this.elemanYonetici.temizle();
    this.zamanKontrol.sifirla();
    this.kapYonetici.temizle();
    this.aktifParcacikSayisi = 0;
    this.frameCounter = 0;

    console.log('[FikoSPHMotor] Temizleme tamamlandı');
  }

  /**
   * Reset simulation - keep config, clear particles and elements
   */
  sifirla(): void {
    console.log('[FikoSPHMotor] Simülasyon sıfırlanıyor...');

    const calisiyor = this.durum === SimulasyonDurumu.CALISIYOR;

    if (calisiyor) {
      this.durdur();
    }

    // Reset subsystems
    this.gpuHesap?.sifirla();
    // Keep elements across resets - only clear particles
    // this.elemanYonetici.temizle();
    this.zamanKontrol.sifirla();
    this.analiz?.sifirla();
    this.sensorGrafik = new FikoSPHSensorGrafik();

    this.aktifParcacikSayisi = 0;
    this.frameCounter = 0;

    console.log('[FikoSPHMotor] Sıfırlama tamamlandı');

    if (calisiyor) {
      this.baslat();
    }
  }

  // ============================================================================
  // MAIN LOOP
  // ============================================================================

  /**
   * Main animation loop - called by requestAnimationFrame
   */
  private animasyonDongusu = (): void => {
    // Schedule next frame immediately to maintain 60 FPS
    this.animasyonId = requestAnimationFrame(this.animasyonDongusu);

    try {
      // If paused, skip simulation but still render
      if (this.durum === SimulasyonDurumu.DURAKLATILDI) {
        if (this.render && this.context && this.gpuHesap) {
          const encoder = this.device!.createCommandEncoder();
          this.renderFrameInternal(encoder);
          this.device!.queue.submit([encoder.finish()]);
        }
        return;
      }

      if (this.durum !== SimulasyonDurumu.CALISIYOR) {
        return;
      }

      // Calculate delta time
      const simdikiZaman = performance.now();
      const gecenZaman = (simdikiZaman - this.sonFrameZamani) / 1000.0; // Convert to seconds
      this.sonFrameZamani = simdikiZaman;

      // Clamp dt to prevent instability
      const dt = Math.min(gecenZaman, 0.033); // Max 33ms (30 FPS minimum)

      // Simulation step
      this.simAdimi(dt);

      // Increment frame counter
      this.frameCounter++;

      // Reset error counter on successful frame
      this.hataSayaci = 0;

    } catch (hata) {
      console.error('[FikoSPHMotor] Animasyon döngüsü hatası:', hata);
      this.hataIsle(hata as Error);
    }
  };

  /**
   * Single simulation step
   */
  private simAdimi(dt: number): void {
    if (!this.device || !this.gpuHesap || !this.render || !this.context) {
      return;
    }

    try {
      // 1. Time control - advance timeline, fire events
      this.zamanKontrol.adim(dt);
      const simDt = dt * this.zamanKontrol.simHizi;
      const simZaman = this.zamanKontrol.simZaman;

      // 2. Update GPU uniforms
      this.gpuHesap.guncelleUniformlar(this.config, simZaman, this.aktifParcacikSayisi);

      // 3. Update element buffers if needed
      if (this._elemanBufferDirty || this.frameCounter % 60 === 0) {
        this.gpuHesap.guncelleElemanBufferlari(this.elemanYonetici);
        this._elemanBufferDirty = false;
      }

      // 3b. Spawn particles from active inlets (CPU-side for now)
      this.girisSpawn(simDt, simZaman);

      // 4. Create command encoder
      const encoder = this.device.createCommandEncoder({
        label: 'SPH Simulation Frame'
      });

      // 5. Encode simulation compute passes
      this.gpuHesap.encodeSimulasyonFrame(encoder, this.aktifParcacikSayisi);

      // 6. Encode render pass
      this.renderFrameInternal(encoder);

      // 7. Submit commands
      this.device.queue.submit([encoder.finish()]);

      // 8. Analysis update
      if (this.analiz) {
        this.analiz.frameTamamlandi();

        // Fire frame completed event
        if (this.olaylar.onFrameTamamlandi) {
          const istatistik = {
            frameCounter: this.frameCounter,
            aktifParcacikSayisi: this.aktifParcacikSayisi,
            simDt: simDt
          };
          this.olaylar.onFrameTamamlandi(istatistik);
        }
      }

      // 9. Async sensor readback at 10Hz
      this.sensorOkumayiKontrolEt(performance.now());

      // 10. Check for filling completion
      if (this.aktifParcacikSayisi >= this.config.global.maxParticle * 0.95) {
        if (this.olaylar.onDolumTamamlandi) {
          this.olaylar.onDolumTamamlandi();
        }
      }

    } catch (hata) {
      console.error('[FikoSPHMotor] Simülasyon adımı hatası:', hata);
      throw hata; // Re-throw to be caught by animation loop
    }
  }

  /**
   * Aktif girişlerden parçacık üret (CPU spawn) — render ve headless döngü ortak.
   * akisBaslangic/akisBitis alanları varsa zamanlı senaryo uygulanır.
   */
  private girisSpawn(simDt: number, simZaman: number): void {
    if (!this.gpuHesap) return;
    const aktifGirisler = this.elemanYonetici.tipGore(ElemanTipi.DOKUM_GIRISI);
    for (const giris of aktifGirisler) {
      if (!giris.aktif) continue;

      const g = giris as any;
      // Zamanlı senaryo: akisBaslangic/akisBitis (s) penceresi dışında spawn yok
      const bas = g.akisBaslangic ?? 0;
      const bit = g.akisBitis ?? 0;
      if (simZaman < bas) continue;
      if (bit > 0 && simZaman > bit) continue;

      const debi = g.debi ?? 500; // particles per second
      const spawnCount = Math.ceil(debi * simDt);

      if (spawnCount <= 0 || this.aktifParcacikSayisi >= this.config.global.maxParticle) continue;

      const actualSpawn = Math.min(spawnCount, this.config.global.maxParticle - this.aktifParcacikSayisi);

      // Generate spawn positions around inlet
      const positions = new Float32Array(actualSpawn * 3);
      const velocities = new Float32Array(actualSpawn * 3);
      const pozX = g.pozisyon?.x ?? 0;
      const pozY = g.pozisyon?.y ?? 0.5;
      const pozZ = g.pozisyon?.z ?? 0;
      const yonX = g.yon?.x ?? 0;
      const yonY = g.yon?.y ?? -1;
      const yonZ = g.yon?.z ?? 0;
      const hiz = g.hiz ?? 2.0;
      const yaricap = g.yaricap ?? 0.05;

      for (let i = 0; i < actualSpawn; i++) {
        // Random offset within inlet radius
        const angle = Math.random() * Math.PI * 2;
        const r = Math.random() * yaricap;
        positions[i * 3] = pozX + Math.cos(angle) * r;
        positions[i * 3 + 1] = pozY + (Math.random() - 0.5) * yaricap * 0.2;
        positions[i * 3 + 2] = pozZ + Math.sin(angle) * r;

        // Velocity along inlet direction with slight randomness
        const hizKaris = hiz * 0.1;
        velocities[i * 3] = yonX * hiz + (Math.random() - 0.5) * hizKaris;
        velocities[i * 3 + 1] = yonY * hiz + (Math.random() - 0.5) * hizKaris;
        velocities[i * 3 + 2] = yonZ * hiz + (Math.random() - 0.5) * hizKaris;
      }

      const spawned = this.gpuHesap.cpuSpawnParcaciklar(
        this.aktifParcacikSayisi,
        positions,
        velocities
      );
      this.aktifParcacikSayisi += spawned;
    }
  }

  /**
   * HEADLESS sabit-adım hesap modu (render/RAF YOK) — dolum köprüsü için.
   * Her adım: uniform güncelle → spawn → grid+solver+integrate+SDF encode →
   * (onAdimEncode ile köprü splat) → submit. UI donmasın diye periyodik yield.
   *
   * @returns toplam simüle edilen süre (s)
   */
  async hesaplaAdimlar(opts: {
    sureSn: number;
    dt: number;
    onAdimEncode?: (encoder: GPUCommandEncoder, simZamanSn: number) => void;
    ilerle?: (yuzde: number) => void;
    durdurmaKontrol?: (simZamanSn: number, parcacik: number) => boolean;
  }): Promise<number> {
    if (!this.device || !this.gpuHesap) {
      throw new Error('Motor hazır değil — önce basla() çağırın.');
    }

    const dt = Math.max(1e-5, opts.dt);
    const adimSayisi = Math.max(1, Math.ceil(opts.sureSn / dt));
    this.config.global.dt = dt;
    this.durumDegistir(SimulasyonDurumu.CALISIYOR);

    let simZaman = 0;
    const YIELD_ADIM = 40; // her N adımda GPU'yu bekle + UI'a nefes + ilerleme

    for (let adim = 0; adim < adimSayisi; adim++) {
      simZaman = (adim + 1) * dt;

      this.gpuHesap.guncelleUniformlar(this.config, simZaman, this.aktifParcacikSayisi);

      if (this._elemanBufferDirty) {
        this.gpuHesap.guncelleElemanBufferlari(this.elemanYonetici);
        this._elemanBufferDirty = false;
      }

      this.girisSpawn(dt, simZaman);

      const encoder = this.device.createCommandEncoder({ label: 'SPH Headless Adim' });
      this.gpuHesap.encodeSimulasyonFrame(encoder, this.aktifParcacikSayisi);
      if (opts.onAdimEncode) opts.onAdimEncode(encoder, simZaman);
      this.device.queue.submit([encoder.finish()]);

      if ((adim + 1) % YIELD_ADIM === 0 || adim === adimSayisi - 1) {
        await this.device.queue.onSubmittedWorkDone();
        if (opts.ilerle) opts.ilerle(((adim + 1) / adimSayisi) * 100);
        if (opts.durdurmaKontrol && opts.durdurmaKontrol(simZaman, this.aktifParcacikSayisi)) break;
        await new Promise(r => setTimeout(r, 0)); // UI nefesi
      }
    }

    await this.device.queue.onSubmittedWorkDone();
    this.durumDegistir(SimulasyonDurumu.TAMAMLANDI);
    return simZaman;
  }

  /**
   * Check if it's time to read sensor data (10Hz max)
   */
  private sensorOkumayiKontrolEt(zaman: number): void {
    if (this.sensorReadbackPending) {
      return;
    }

    if (zaman - this.sonSensorOkumaZamani < this.SENSOR_READBACK_INTERVAL_MS) {
      return;
    }

    this.sonSensorOkumaZamani = zaman;
    this.sensorReadbackPending = true;

    // Async readback
    this.sensorVerileriniOku().then(() => {
      this.sensorReadbackPending = false;
    }).catch((hata) => {
      console.error('[FikoSPHMotor] Sensor okuma hatası:', hata);
      this.sensorReadbackPending = false;
    });
  }

  /**
   * Async sensor data readback from GPU
   */
  private async sensorVerileriniOku(): Promise<void> {
    if (!this.gpuHesap || !this.analiz || !this.sensorGrafik) {
      return;
    }

    try {
      const sensorlar = this.elemanYonetici.tipGore(ElemanTipi.SANAL_SENSOR);

      for (const sensor of sensorlar) {
        const veri = await this.gpuHesap.sensorOku(sensor.id);

        if (veri) {
          // Store sensor data (simplified, no direct analiz API)
          // this.sensorGrafik.veriEkle(sensor.id, this.zamanKontrol.simZaman, veri);
        }
      }
    } catch (hata) {
      console.error('[FikoSPHMotor] Sensor verileri okunamadı:', hata);
    }
  }

  // ============================================================================
  // SOLVER
  // ============================================================================

  /**
   * Change solver at runtime (hot-swap)
   */
  async solverDegistir(tip: SolverTipi): Promise<void> {
    if (!this.device || !this.gpuHesap) {
      console.error('[FikoSPHMotor] GPU henüz hazır değil');
      return;
    }

    try {
      console.log('[FikoSPHMotor] Solver değiştiriliyor:', tip);

      const calisiyor = this.durum === SimulasyonDurumu.CALISIYOR;
      if (calisiyor) {
        this.duraklat();
      }

      // Swap solver in GPU compute (handles create → basla → buffers → bindGroup)
      await this.gpuHesap.solverDegistir(tip);

      // Update config
      this.config.global.solverTipi = tip;

      console.log('[FikoSPHMotor] Solver değiştirildi:', tip);

      if (this.olaylar.onSolverDegisti) {
        this.olaylar.onSolverDegisti(tip);
      }

      if (calisiyor) {
        this.devamEt();
      }

    } catch (hata) {
      console.error('[FikoSPHMotor] Solver değiştirme hatası:', hata);
      throw hata;
    }
  }

  /**
   * Get current solver type
   */
  getSolverTipi(): SolverTipi {
    return this.config.global.solverTipi;
  }

  // ============================================================================
  // ELEMENTS
  // ============================================================================

  /**
   * Add inlet (döküm girişi)
   */
  dokumGirisiEkle(config?: any): string {
    const eleman = new FikoDokumGirisi(config);
    this.elemanYonetici.ekle(eleman);
    this._elemanBufferDirty = true;

    if (this.olaylar.onElemanEklendi) {
      this.olaylar.onElemanEklendi(eleman.id, ElemanTipi.DOKUM_GIRISI);
    }

    console.log('[FikoSPHMotor] Döküm girişi eklendi:', eleman.id, config);
    return eleman.id;
  }

  /**
   * Add air vent (hava deliği)
   */
  havaDeligiEkle(config?: any): string {
    const eleman = new FikoHavaDeligi(config);
    this.elemanYonetici.ekle(eleman);
    this._elemanBufferDirty = true;

    if (this.olaylar.onElemanEklendi) {
      this.olaylar.onElemanEklendi(eleman.id, ElemanTipi.HAVA_DELIGI);
    }

    console.log('[FikoSPHMotor] Hava deliği eklendi:', eleman.id, config);
    return eleman.id;
  }

  /**
   * Add obstacle (engel)
   */
  engelEkle(config?: any): string {
    const eleman = new FikoEngel(config);
    this.elemanYonetici.ekle(eleman);
    this._elemanBufferDirty = true;

    if (this.olaylar.onElemanEklendi) {
      this.olaylar.onElemanEklendi(eleman.id, ElemanTipi.ENGEL);
    }

    console.log('[FikoSPHMotor] Engel eklendi:', eleman.id, config);
    return eleman.id;
  }

  /**
   * Add filter (filtre)
   */
  filtreEkle(config?: any): string {
    const eleman = new FikoFiltre(config);
    this.elemanYonetici.ekle(eleman);
    this._elemanBufferDirty = true;

    if (this.olaylar.onElemanEklendi) {
      this.olaylar.onElemanEklendi(eleman.id, ElemanTipi.FILTRE);
    }

    console.log('[FikoSPHMotor] Filtre eklendi:', eleman.id, config);
    return eleman.id;
  }

  /**
   * Add feeder (besleyici)
   */
  besleyiciEkle(config?: any): string {
    const eleman = new FikoBesleyici(config);
    this.elemanYonetici.ekle(eleman);
    this._elemanBufferDirty = true;

    if (this.olaylar.onElemanEklendi) {
      this.olaylar.onElemanEklendi(eleman.id, ElemanTipi.BESLEYICI);
    }

    console.log('[FikoSPHMotor] Besleyici eklendi:', eleman.id, config);
    return eleman.id;
  }

  /**
   * Add outlet (çıkış)
   */
  cikisEkle(config?: any): string {
    const eleman = new FikoCikis(config);
    this.elemanYonetici.ekle(eleman);
    this._elemanBufferDirty = true;

    if (this.olaylar.onElemanEklendi) {
      this.olaylar.onElemanEklendi(eleman.id, ElemanTipi.CIKIS);
    }

    console.log('[FikoSPHMotor] Çıkış eklendi:', eleman.id, config);
    return eleman.id;
  }

  /**
   * Add sensor
   */
  sensorEkle(config?: any): string {
    const eleman = new FikoSanalSensor(config);
    this.elemanYonetici.ekle(eleman);
    this._elemanBufferDirty = true;

    // Register sensor in grafik
    if (this.sensorGrafik) {
      this.sensorGrafik.sensorEkle(eleman.id, config?.isim || eleman.ad, 0);
    }

    if (this.olaylar.onElemanEklendi) {
      this.olaylar.onElemanEklendi(eleman.id, ElemanTipi.SANAL_SENSOR);
    }

    console.log('[FikoSPHMotor] Sensor eklendi:', eleman.id, config);
    return eleman.id;
  }

  /**
   * Add thermal element (termal eleman - heater/cooler)
   */
  termalElemanEkle(config?: any): string {
    const eleman = new FikoTermalEleman(config);
    this.elemanYonetici.ekle(eleman);
    this._elemanBufferDirty = true;

    if (this.olaylar.onElemanEklendi) {
      this.olaylar.onElemanEklendi(eleman.id, ElemanTipi.TERMAL_ELEMAN);
    }

    console.log('[FikoSPHMotor] Termal eleman eklendi:', eleman.id, config);
    return eleman.id;
  }

  /**
   * Remove element by ID
   */
  elemanSil(id: string): void {
    const eleman = this.elemanYonetici.al(id);
    if (!eleman) {
      console.warn('[FikoSPHMotor] Eleman bulunamadı:', id);
      return;
    }

    const tip = eleman.tip;
    this.elemanYonetici.sil(id);
    this._elemanBufferDirty = true;

    // Remove from sensor grafik
    if (tip === ElemanTipi.SANAL_SENSOR && this.sensorGrafik) {
      this.sensorGrafik.sensorSil(id);
    }

    if (this.olaylar.onElemanSilindi) {
      this.olaylar.onElemanSilindi(id);
    }

    console.log('[FikoSPHMotor] Eleman silindi:', id, tip);
  }

  /**
   * Update element parameter
   */
  elemanGuncelle(id: string, parametre: string, deger: any): void {
    const eleman = this.elemanYonetici.al(id);
    if (!eleman) {
      console.warn('[FikoSPHMotor] Eleman bulunamadı:', id);
      return;
    }

    // Update parameter using element's method if available
    if (eleman.parametreAyarla) {
      eleman.parametreAyarla(parametre, deger);
    } else {
      // Fallback to direct property assignment
      (eleman as any)[parametre] = deger;
    }
    this._elemanBufferDirty = true;

    console.log('[FikoSPHMotor] Eleman güncellendi:', id, parametre, deger);
  }

  /**
   * Get list of elements (optionally filtered by type)
   */
  elemanListesi(tip?: ElemanTipi): any[] {
    if (tip) {
      return this.elemanYonetici.tipGore(tip);
    }
    return this.elemanYonetici.hepsi();
  }

  // ============================================================================
  // CONTAINER
  // ============================================================================

  /**
   * Set container mesh for SDF collision
   */
  async kapAyarla(vertices: Float32Array, indices?: Uint32Array, id?: string, ad?: string): Promise<void> {
    if (!this.device) {
      console.error('[FikoSPHMotor] GPU henüz hazır değil');
      return;
    }

    const kapId = id || `kap_${Date.now()}`;
    const kapAd = ad || 'Varsayılan Kap';

    console.log('[FikoSPHMotor] Kap ayarlanıyor...', {
      id: kapId,
      vertexCount: vertices.length / 3,
      indexCount: indices ? indices.length : 0
    });

    try {
      // Build SDF via KapYonetici
      await this.kapYonetici.kapEkle(kapId, kapAd, vertices, indices);
      this.kapYonetici.setAktifKap(kapId);

      // Upload SDF buffer to GPU compute for collision
      if (this.gpuHesap) {
        const sdfBuffer = this.kapYonetici.getSdfBuffer();
        if (sdfBuffer) {
          this.gpuHesap.kapSDFYukle(sdfBuffer, this.kapYonetici.getSdfInfoBuffer() ?? undefined);
        }
      }

      console.log('[FikoSPHMotor] Kap ayarlandı:', kapId);

    } catch (hata) {
      console.error('[FikoSPHMotor] Kap ayarlama hatası:', hata);
      throw hata;
    }
  }

  /**
   * CPU'da hazırlanmış SDF verisini doğrudan yükle (voxel kafesten mesafe
   * dönüşümü — mesh→SDF GPU taramasından hızlı ve dolum kafesiyle hizalı).
   * Konvansiyon: kavite içi POZİTİF, dışı NEGATİF (çarpışma shader'ı
   * distance<0'da parçacığı gradyan yönünde içeri iter).
   */
  kapSDFDogrudanYukle(
    sdf: Float32Array,
    info: { origin: [number, number, number]; cellSize: number; boyut: [number, number, number] },
  ): void {
    if (!this.device || !this.gpuHesap) {
      console.error('[FikoSPHMotor] GPU henüz hazır değil');
      return;
    }
    this.dogrudanSdfBuffer?.destroy();
    this.dogrudanSdfInfoBuffer?.destroy();

    this.dogrudanSdfBuffer = this.device.createBuffer({
      label: 'Dogrudan SDF Data',
      size: sdf.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(this.dogrudanSdfBuffer, 0, sdf);

    // SDFInfo struct: 64B — origin_xyz+cellSize (f32×4) + dim_xyz+totalCells (u32×4) + pad
    const infoData = new ArrayBuffer(64);
    const f = new Float32Array(infoData);
    const u = new Uint32Array(infoData);
    f[0] = info.origin[0]; f[1] = info.origin[1]; f[2] = info.origin[2]; f[3] = info.cellSize;
    u[4] = info.boyut[0]; u[5] = info.boyut[1]; u[6] = info.boyut[2];
    u[7] = info.boyut[0] * info.boyut[1] * info.boyut[2];
    this.dogrudanSdfInfoBuffer = this.device.createBuffer({
      label: 'Dogrudan SDF Info',
      size: 64,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(this.dogrudanSdfInfoBuffer, 0, infoData);

    this.gpuHesap.kapSDFYukle(this.dogrudanSdfBuffer, this.dogrudanSdfInfoBuffer);
    console.log('[FikoSPHMotor] Doğrudan SDF yüklendi:', info.boyut.join('×'));
  }

  // ============================================================================
  // CONFIG
  // ============================================================================

  /**
   * Update configuration at runtime
   */
  configGuncelle(config: Partial<SPHSimulasyonConfig>): void {
    // Deep merge global parameters
    if (config.global) {
      Object.assign(this.config.global, config.global);
    }
    if (config.render) {
      Object.assign(this.config.render, config.render);
    }
    if (config.dfsph) {
      Object.assign(this.config.dfsph, config.dfsph);
    }
    if (config.pbf) {
      Object.assign(this.config.pbf, config.pbf);
    }
    if (config.pcisph) {
      Object.assign(this.config.pcisph, config.pcisph);
    }
    if (config.termal) {
      Object.assign(this.config.termal, config.termal);
    }
    if (config.gaz) {
      Object.assign(this.config.gaz, config.gaz);
    }

    // Propagate render settings
    if (config.render && this.render) {
      this.render.guncelleAyarlar(config.render as any);
    }

    console.log('[FikoSPHMotor] Config güncellendi');
  }

  /**
   * Set quality preset
   */
  kaliteAyarla(kalite: KaliteSeviyesi): void {
    console.log('[FikoSPHMotor] Kalite ayarlanıyor:', kalite);

    // Update config based on quality level using correct SPHGlobalParametreler fields
    switch (kalite) {
      case KaliteSeviyesi.DUSUK:
        this.configGuncelle({ global: { smoothingRadius: 0.1, dt: 0.005, kalite } as any });
        break;
      case KaliteSeviyesi.ORTA:
        this.configGuncelle({ global: { smoothingRadius: 0.075, dt: 0.002, kalite } as any });
        break;
      case KaliteSeviyesi.YUKSEK:
        this.configGuncelle({ global: { smoothingRadius: 0.05, dt: 0.001, kalite } as any });
        break;
      case KaliteSeviyesi.ULTRA:
        this.configGuncelle({ global: { smoothingRadius: 0.04, dt: 0.0005, kalite } as any });
        break;
    }
  }

  /**
   * Set gravity vector
   */
  yerCekimiAyarla(x: number, y: number, z: number): void {
    this.config.global.gravity = { x, y, z };
    console.log('[FikoSPHMotor] Yerçekimi ayarlandı:', this.config.global.gravity);
  }

  /**
   * Get current configuration
   */
  getConfig(): SPHSimulasyonConfig {
    return { ...this.config };
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  /**
   * Set render mode
   */
  renderModuAyarla(modu: RenderModu): void {
    if (!this.render) {
      console.warn('[FikoSPHMotor] Render sistemi henüz hazır değil');
      return;
    }

    this.render.guncelleAyarlar({ modu });
    console.log('[FikoSPHMotor] Render modu:', modu);
  }

  /**
   * Configure heatmap visualization
   */
  heatmapAyarla(tipi: number, aktif: boolean): void {
    if (!this.render) {
      console.warn('[FikoSPHMotor] Render sistemi henüz hazır değil');
      return;
    }

    this.render.guncelleAyarlar({ heatmapTipi: tipi, heatmapAktif: aktif });
    console.log('[FikoSPHMotor] Heatmap:', { tipi, aktif });
  }

  // ============================================================================
  // ANALYSIS
  // ============================================================================

  /**
   * Get current statistics
   */
  getIstatistikler(): any {
    if (!this.analiz) {
      return null;
    }

    return this.analiz.getIstatistikler();
  }

  /**
   * Get sensor data
   */
  getSensorVerileri(sensorId: string): any {
    // getSensorVerileri not available on analiz
    // Use getGecmis() or return from sensorGrafik if available
    return null;
  }

  /**
   * Generate analysis report
   */
  raporOlustur(): any {
    if (!this.analiz) {
      return null;
    }

    return this.analiz.exportJSON();
  }

  // ============================================================================
  // TIMELINE
  // ============================================================================

  /**
   * Load timeline scenario
   */
  senaryoYukle(senaryo: any): void {
    this.zamanKontrol.senaryoYukle(senaryo);
    console.log('[FikoSPHMotor] Senaryo yüklendi:', senaryo);
  }

  /**
   * Save current timeline scenario
   */
  senaryoKaydet(): any {
    return this.zamanKontrol.senaryoKaydet();
  }

  /**
   * Set simulation speed multiplier
   */
  simHiziAyarla(hiz: number): void {
    this.zamanKontrol.hiziAyarla(hiz);
    console.log('[FikoSPHMotor] Simülasyon hızı:', hiz);
  }

  // ============================================================================
  // EVENTS
  // ============================================================================

  /**
   * Register event callback
   */
  on(olay: keyof MotorOlaylari, callback: Function): void {
    (this.olaylar as any)[olay] = callback;
  }

  // ============================================================================
  // SERIALIZATION
  // ============================================================================

  /**
   * Export entire simulation state to JSON
   */
  exportJSON(): string {
    const state = {
      version: '1.0',
      config: this.config,
      elemanlar: this.elemanYonetici.toJSON(),
      senaryo: this.zamanKontrol.senaryoKaydet(),
      aktifParcacikSayisi: this.aktifParcacikSayisi,
      simZaman: this.zamanKontrol.simZaman
    };

    return JSON.stringify(state, null, 2);
  }

  /**
   * Import simulation state from JSON
   */
  importJSON(json: string): void {
    try {
      const state = JSON.parse(json);

      console.log('[FikoSPHMotor] Import başlatılıyor...', state);

      const calisiyor = this.durum === SimulasyonDurumu.CALISIYOR;
      if (calisiyor) {
        this.durdur();
      }

      // Restore config
      this.config = { ...varsayilanSPHConfig(), ...state.config };
      this.configGuncelle(this.config);

      // Restore elements
      this.elemanYonetici.temizle();
      // Note: deserialize not implemented, elements need manual restoration
      // this.elemanYonetici.deserialize(state.elemanlar);
      this._elemanBufferDirty = true;

      // Restore scenario
      if (state.senaryo) {
        this.zamanKontrol.senaryoYukle(state.senaryo);
      }

      console.log('[FikoSPHMotor] Import tamamlandı');

      if (calisiyor) {
        this.baslat();
      }

    } catch (hata) {
      console.error('[FikoSPHMotor] Import hatası:', hata);
      throw hata;
    }
  }

  /**
   * Get current simulation state
   */
  getDurum(): SimulasyonDurumu {
    return this.durum;
  }

  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================

  /**
   * Internal render helper - encodes render pass into command encoder
   */
  private renderFrameInternal(encoder: GPUCommandEncoder): void {
    if (!this.render || !this.context || !this.gpuHesap) {
      return;
    }

    try {
      const outputTexture = this.context.getCurrentTexture();
      const outputView = outputTexture.createView();

      const posBuffer = this.gpuHesap.getPositionBuffer();
      const velBuffer = this.gpuHesap.getVelocityBuffer();
      const densBuffer = this.gpuHesap.getDensityBuffer();
      const heatmapBuffer = this.gpuHesap.getHeatmapBuffer();

      if (!posBuffer || !velBuffer || !densBuffer) {
        return;
      }

      this.render.renderFrame(
        encoder,
        outputView,
        this.kamera,
        posBuffer,
        velBuffer,
        densBuffer,
        heatmapBuffer!,
        this.aktifParcacikSayisi
      );
    } catch (hata) {
      console.error('[FikoSPHMotor] Render hatası:', hata);
    }
  }

  /**
   * Update camera data from external source (e.g. Three.js camera)
   */
  kameraGuncelle(kamera: KameraData): void {
    this.kamera.view.set(kamera.view);
    this.kamera.proj.set(kamera.proj);
    this.kamera.invProj.set(kamera.invProj);
    this.kamera.pos[0] = kamera.pos[0];
    this.kamera.pos[1] = kamera.pos[1];
    this.kamera.pos[2] = kamera.pos[2];
  }

  /**
   * Get active particle count
   */
  getAktifParcacikSayisi(): number {
    return this.aktifParcacikSayisi;
  }

  /** WebGPU device (köprü/splat gibi dış compute pass'ler için) */
  getDevice(): GPUDevice | null {
    return this.device;
  }

  /** Parçacık pozisyon buffer'ı (köprü splat girişi) */
  getPositionBuffer(): GPUBuffer | null {
    return this.gpuHesap?.getPositionBuffer() ?? null;
  }

  /**
   * Get current simulation time
   */
  getSimZaman(): number {
    return this.zamanKontrol.simZaman;
  }

  /**
   * Change state and fire event
   */
  private durumDegistir(yeniDurum: SimulasyonDurumu): void {
    const eskiDurum = this.durum;
    this.durum = yeniDurum;

    console.log('[FikoSPHMotor] Durum:', eskiDurum, '->', yeniDurum);

    if (this.olaylar.onDurumDegisti) {
      this.olaylar.onDurumDegisti(yeniDurum);
    }
  }

  /**
   * Handle errors gracefully
   */
  private hataIsle(hata: Error): void {
    console.error('[FikoSPHMotor] HATA:', hata);

    this.hataSayaci++;

    if (this.hataSayaci > this.MAX_HATA_SAYISI) {
      console.error('[FikoSPHMotor] Çok fazla hata! Simülasyon durduruluyor.');
      this.durdur();
      this.durumDegistir(SimulasyonDurumu.HATA);
    }

    if (this.olaylar.onHata) {
      this.olaylar.onHata(hata);
    }
  }

  /**
   * Setup default camera matrices
   */
  private setupVarsayilanKamera(width: number, height: number): void {
    const aspect = (width || 800) / (height || 600);
    const fov = Math.PI / 4; // 45 degrees
    const near = 0.01;
    const far = 100;

    // Camera position: looking at origin from (0, 1, 3)
    const eyeX = 0, eyeY = 1, eyeZ = 3;

    // Simple lookAt view matrix (eye at (0,1,3), target at (0,0,0), up (0,1,0))
    const fx = 0 - eyeX, fy = 0 - eyeY, fz = 0 - eyeZ;
    const fl = Math.sqrt(fx*fx + fy*fy + fz*fz);
    const fxn = fx/fl, fyn = fy/fl, fzn = fz/fl;

    // right = cross(forward, up)
    const rx = fyn * 0 - fzn * 1;
    const ry = fzn * 0 - fxn * 0;
    const rz = fxn * 1 - fyn * 0;
    const rl = Math.sqrt(rx*rx + ry*ry + rz*rz);
    const rxn = rx/rl, ryn = ry/rl, rzn = rz/rl;

    // up = cross(right, forward)
    const ux = ryn * fzn - rzn * fyn;
    const uy = rzn * fxn - rxn * fzn;
    const uz = rxn * fyn - ryn * fxn;

    // View matrix (column-major)
    this.kamera.view.set([
      rxn, ux, -fxn, 0,
      ryn, uy, -fyn, 0,
      rzn, uz, -fzn, 0,
      -(rxn*eyeX + ryn*eyeY + rzn*eyeZ),
      -(ux*eyeX + uy*eyeY + uz*eyeZ),
      -(-fxn*eyeX + -fyn*eyeY + -fzn*eyeZ),
      1
    ]);

    // Perspective projection matrix (column-major)
    const f = 1.0 / Math.tan(fov / 2);
    const rangeInv = 1.0 / (near - far);
    this.kamera.proj.set([
      f / aspect, 0, 0, 0,
      0, f, 0, 0,
      0, 0, (near + far) * rangeInv, -1,
      0, 0, 2 * near * far * rangeInv, 0
    ]);

    // Inverse projection (simplified)
    this.kamera.invProj.set([
      aspect / f, 0, 0, 0,
      0, 1/f, 0, 0,
      0, 0, 0, 1/(2*near*far*rangeInv),
      0, 0, -1, (near+far)/(2*near*far)
    ]);

    this.kamera.pos = [eyeX, eyeY, eyeZ];
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export type {
  SPHSimulasyonConfig
} from './FikoSPHParametreler';

export {
  SolverTipi,
  KaliteSeviyesi,
  varsayilanSPHConfig
} from './FikoSPHParametreler';

export { ElemanTipi } from './elemanlar/ISPHEleman';
export { RenderModu } from './render/FikoSPHRender';
